import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { analytics } from '../utils/analytics';
import { logger } from '../utils/logger';
import { API } from '../utils/apiBase';


// Landing page for magic-link emails. Exchanges ?token=XXX for a session
// cookie and forwards the user to /dashboard. One-time use.
const MagicLinkCallback = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refreshAuth } = useAuth();
  const [error, setError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    // Prevent double-submit under React StrictMode.
    if (ran.current) return;
    ran.current = true;

    const token = params.get('token');
    if (!token) {
      setError('This link is missing a sign-in token. Request a new one from the login page.');
      return;
    }

    (async () => {
      try {
        await axios.post(`${API}/auth/magic-link/verify`, { token }, { withCredentials: true });
        await refreshAuth();
        analytics.login('magic_link');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const msg = err.response?.data?.detail || 'We couldn\'t sign you in with this link. Request a new one.';
        logger.error('Magic-link verify failed:', msg);
        setError(msg);
      }
    })();
  }, [params, navigate, refreshAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="magic-link-error">
        <div className="max-w-md w-full text-center">
          <AlertTriangle size={48} className="text-[#e11d48] mx-auto mb-4" />
          <h1 className="font-satoshi text-2xl font-black text-[#0f1115] mb-3">Sign-in link didn't work</h1>
          <p className="text-[#4b5563] mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-block bg-[#002fa7] text-white px-6 py-3 font-semibold hover:bg-[#001f70] transition-colors"
            data-testid="magic-link-back-to-login"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="magic-link-loading">
      <div className="text-center">
        <Loader2 size={48} className="animate-spin text-[#002fa7] mx-auto mb-4" />
        <p className="text-[#4b5563]">Signing you in&hellip;</p>
      </div>
    </div>
  );
};

export default MagicLinkCallback;
